import { z } from 'zod';
import { UserRoleSchema } from '../enums/user-role.js';
import { IsoDateStringSchema, UuidV7Schema } from './common.js';

export const DashboardFiltersSchema = z.object({
  partnerId: UuidV7Schema.optional(),
  managerId: UuidV7Schema.optional(),
  superviseurId: UuidV7Schema.optional(),
  commercialId: UuidV7Schema.optional(),
  /** Magasin de la fiche (réseaux de boutiques) — même filtre que la liste. */
  storeId: UuidV7Schema.optional(),
  verticaleId: UuidV7Schema.optional(),
  /**
   * Comma-separated list of verticale UUIDs to **include**. Used by the
   * dashboard verticale chips so toggling a chip filters the whole page
   * (KPIs, activity, top-performers), not just the charts. When unset the
   * dashboard considers all verticales visible. An explicit empty / nil
   * UUID value means "match no verticale" (front sends NIL UUID when the
   * user hid every chip). Applied as an AND-clause alongside the
   * single-select `verticaleId` (drawer) when both are present.
   */
  verticaleIds: z.string().optional(),
  from: IsoDateStringSchema.optional(),
  to: IsoDateStringSchema.optional(),
});
export type DashboardFilters = z.infer<typeof DashboardFiltersSchema>;

/**
 * Per-V2-status counts (modèle 8 états, cf. enums/lead-status.ts) ; utilisé
 * par le funnel pour afficher des nombres réels et non des ratios. Chaque
 * compteur est cumulatif sur la période courante du filtre dashboard.
 */
export const KpiByStatusSchema = z.object({
  created: z.number().int().min(0),
  scheduled: z.number().int().min(0),
  in_delivery: z.number().int().min(0),
  delivered: z.number().int().min(0),
  delivery_failed: z.number().int().min(0),
  cancelled: z.number().int().min(0),
  /** @deprecated statut de vente pré-refonte, conservé pour la donnée historique. */
  sold: z.number().int().min(0),
  not_sold: z.number().int().min(0),
  sale_validated: z.number().int().min(0),
  sale_confirmed: z.number().int().min(0),
  sale_rejected: z.number().int().min(0),
});
export type KpiByStatus = z.infer<typeof KpiByStatusSchema>;

/**
 * Ventilation des non-vente par raison. Renvoyé **uniquement** quand
 * l'observer est admin (sinon `undefined`). Permet au funnel admin
 * d'afficher la sous-info "KO partenaire 0" sous Non vente sans exposer
 * le signal de qualité partenaire aux below-line.
 */
export const KpiByNonVenteReasonSchema = z.object({
  ko_partenaire: z.number().int().min(0),
});
export type KpiByNonVenteReason = z.infer<typeof KpiByNonVenteReasonSchema>;

export const RatingDistributionSchema = z.object({
  1: z.number().int().min(0),
  2: z.number().int().min(0),
  3: z.number().int().min(0),
  4: z.number().int().min(0),
  5: z.number().int().min(0),
});

const KpiSnapshotCore = {
  totalLeads: z.number().int().min(0),
  totalContracts: z.number().int().min(0),
  conversionRate: z.number().min(0).max(1),
  /**
   * @deprecated Somme des `prixVente` figés sur les ventes activées (admin,
   * 0 sinon). Plus affiché : les chiffres d'argent vivent dans le tableau de
   * bord Finance (`/finance/summary`, grand livre). Conservé pour les clients
   * déjà déployés. `leadRevenue`, `netMargin` et `balance` (CA « toutes
   * fiches transmises » et marges qui en découlaient) ont été retirés le
   * 06/10/2026 : ils additionnaient des prix de vente sur des fiches jamais
   * vendues.
   */
  revenue: z.number().min(0),
  /** @deprecated Somme des `commission` figées sur les ventes activées (admin). Plus affiché. */
  commission: z.number().min(0).optional(),
  byStatus: KpiByStatusSchema,
  /** Admin-only : ventilation des non-vente par raison (cf. KpiByNonVenteReasonSchema). */
  byNonVenteReason: KpiByNonVenteReasonSchema.optional(),
  avgDurationMs: z.number().nullable().optional(),
  avgRating: z.number().nullable().optional(),
  ratingCount: z.number().int().min(0).optional(),
  ratingDistribution: RatingDistributionSchema.optional(),
};

export const KpiSnapshotSchema = z.object(KpiSnapshotCore);
export type KpiSnapshot = z.infer<typeof KpiSnapshotSchema>;

export const DashboardKpisSchema = z.object({
  ...KpiSnapshotCore,
  /** Previous period snapshot (same duration) — null when no range was provided. */
  previous: KpiSnapshotSchema.nullable().optional(),
});
export type DashboardKpis = z.infer<typeof DashboardKpisSchema>;

export const ActivityPointSchema = z.object({
  date: IsoDateStringSchema,
  leads: z.number().int().min(0),
  contracts: z.number().int().min(0),
  /** Sum of prixVente for leads transmitted that day (lead revenue). */
  revenue: z.number().min(0).optional(),
  /**
   * Ventilation par verticale du jour. Map `verticaleId → counts`. Permet
   * au front de superposer une bande/bâton stacké coloré par verticale
   * sous la courbe totale, en s'alignant sur la palette du donut. Vide
   * pour les leads sans verticaleId (data legacy v1).
   */
  byVerticale: z
    .array(
      z.object({
        verticaleId: UuidV7Schema,
        slug: z.string(),
        leads: z.number().int().min(0),
        contracts: z.number().int().min(0),
        revenue: z.number().min(0).optional(),
      }),
    )
    .optional(),
});
export type ActivityPoint = z.infer<typeof ActivityPointSchema>;

/**
 * Verticale-based lead breakdown. Keyed on the stable UUID — admin can
 * rename a verticale in the config panel without breaking the chart color
 * or the underlying aggregation. `slug` drives the brand accent; `name`
 * is display-only.
 */
export const VerticaleBreakdownSchema = z.object({
  verticaleId: UuidV7Schema,
  slug: z.string(),
  name: z.string(),
  count: z.number().int().min(0),
  /** Number of sold leads per verticale (status in converted/vente). */
  salesCount: z.number().int().min(0).optional(),
  /** Sum of prixVente on all leads in range (lead revenue). */
  revenue: z.number().min(0).optional(),
  /** Sum of commission on sold leads only (rétrocom). */
  commission: z.number().min(0).optional(),
});
export type VerticaleBreakdown = z.infer<typeof VerticaleBreakdownSchema>;

/**
 * Top performer entry. Note la shape `id` + `firstName` + `lastName` + `role` :
 * c'est ce que reconnaît `AnonymizeInterceptor` pour anonymiser automatiquement
 * (admin/RP qui voit un manager/sup/vendeur reçoit le matricule à la place).
 * Une shape précédente (`userId` + `displayName` precomputed) leakait les vrais
 * noms aux above-line — corrigé en mai 2026.
 */
export const TopPerformerSchema = z.object({
  id: UuidV7Schema,
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  role: UserRoleSchema,
  /** Matricule Doorly (cf. schemas/matricule.ts). */
  matricule: z.string(),
  leads: z.number().int().min(0),
  contracts: z.number().int().min(0),
  /** Optionnel — enrichissement pour l'affichage dashboard (partenaire). */
  partnerName: z.string().nullable().optional(),
});
export type TopPerformer = z.infer<typeof TopPerformerSchema>;

export const TopPartnerSchema = z.object({
  partnerId: UuidV7Schema,
  name: z.string(),
  leads: z.number().int().min(0),
  revenue: z.number().min(0),
  commission: z.number().min(0),
});
export type TopPartner = z.infer<typeof TopPartnerSchema>;

// ─────────────────────────────────────────────────────────────────────────
// Réseau « click and reveal » (GET /dashboard/network)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Niveau de la ventilation renvoyée, déduit des filtres actifs + du rôle :
 *  - `partners`          : un partenaire par ligne (admin sans partenaire
 *                          choisi, agrégateur sur ses réseaux) ;
 *  - `managers`          : les managers d'un partenaire ;
 *  - `under_manager`     : les superviseurs d'un manager + ses vendeurs en
 *                          rattachement direct (sans superviseur) ;
 *  - `under_superviseur` : les vendeurs d'un superviseur ;
 *  - `none`              : rien en dessous (un vendeur est sélectionné).
 */
export const NetworkLevelSchema = z.enum([
  'partners',
  'managers',
  'under_manager',
  'under_superviseur',
  'none',
]);
export type NetworkLevel = z.infer<typeof NetworkLevelSchema>;

const NetworkStats = {
  /** Fiches attribuées sur la période (règle « son équipe ET lui »). */
  leads: z.number().int().min(0),
  /** Ventes activées (statuts commissionnables). */
  contracts: z.number().int().min(0),
  conversionRate: z.number().min(0).max(1),
  /**
   * Date de la fiche la plus récente SUR LA PÉRIODE filtrée, null si aucune.
   * Bornée par la période : « il y a 12 j » sur un preset 30 j ne dit rien
   * d'une fiche à 35 j — l'UI l'annonce comme telle.
   */
  lastLeadAt: IsoDateStringSchema.nullable(),
};
export const NetworkStatsSchema = z.object(NetworkStats);
export type NetworkStats = z.infer<typeof NetworkStatsSchema>;

/**
 * Ligne « personne ». Shape `id` + `role` (+ `partnerId`) volontairement
 * alignée sur les users embarqués ailleurs : l'AnonymizeInterceptor la
 * reconnaît et réécrit les noms des below-line pour un observer above-line.
 */
export const NetworkUserRowSchema = z.object({
  kind: z.literal('user'),
  id: UuidV7Schema,
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  role: UserRoleSchema,
  matricule: z.string(),
  partnerId: UuidV7Schema,
  partnerName: z.string().nullable(),
  createdAt: IsoDateStringSchema,
  ...NetworkStats,
});
export type NetworkUserRow = z.infer<typeof NetworkUserRowSchema>;

export const NetworkPartnerRowSchema = z.object({
  kind: z.literal('partner'),
  id: UuidV7Schema,
  name: z.string(),
  ...NetworkStats,
});
export type NetworkPartnerRow = z.infer<typeof NetworkPartnerRowSchema>;

export const NetworkRowSchema = z.discriminatedUnion('kind', [
  NetworkUserRowSchema,
  NetworkPartnerRowSchema,
]);
export type NetworkRow = z.infer<typeof NetworkRowSchema>;

export const NetworkBreakdownSchema = z.object({
  level: NetworkLevelSchema,
  /**
   * La personne sélectionnée (manager ou superviseur), avec SES PROPRES fiches
   * uniquement (`commercialId`), pour distinguer ce qu'elle saisit elle-même
   * de ce que produit son équipe. Null aux niveaux `partners` / `managers` /
   * `none`.
   */
  parent: NetworkUserRowSchema.nullable(),
  rows: z.array(NetworkRowSchema),
  /** Tout le périmètre filtré — le dénominateur des parts par ligne. */
  total: NetworkStatsSchema,
  /**
   * Le résidu qu'aucune ligne ne porte (fiches saisies par un RP / un admin,
   * par un compte archivé, ou d'un partenaire hors lignes). Invariant :
   * `sum(rows) + unattributed = total` — sans recouvrement entre lignes.
   */
  unattributed: NetworkStatsSchema,
});
export type NetworkBreakdown = z.infer<typeof NetworkBreakdownSchema>;
